import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { KLayout, KDemo } from "@/pages/kreonis/KLayout";
import { Seo } from "@/components/Seo";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, Sparkles } from "lucide-react";
import { toast } from "sonner";

const tiers = [
  { id: "core", name: "CORE", tagline: "Always Free", m: 0, y: 0, cta: "Access Core Dashboard", popular: false,
    features: ["Main markets only", "4-hour data delay", "3 AI Analyst reports / mo", "Base Simulation Workspace"] },
  { id: "alpha", name: "ALPHA PRO", tagline: "Most Popular", m: 49, y: 399, cta: "Upgrade to Alpha Pro", popular: true,
    monthly_lookup: "pro_monthly", yearly_lookup: "pro_yearly",
    features: ["Real-time Intel Feed", "Unlimited AI Analyst reports", "Advanced Simulation Workspace", "Up to 3 years backtesting"] },
  { id: "quant", name: "QUANT ADVANCED", tagline: "Institutional", m: 149, y: 1199, cta: "Upgrade to Quant Advanced", popular: false,
    monthly_lookup: "premium_monthly", yearly_lookup: "premium_yearly",
    features: ["Maximum priority delivery", "Unlimited historical backtesting", "CSV / Excel data export", "Priority chat support"] },
];

export default function KPricing() {
  const [annual, setAnnual] = useState(false);
  const [loading, setLoading] = useState("");
  const { user } = useAuth();
  const navigate = useNavigate();

  const choose = async (t) => {
    if (t.id === "core") { navigate(user ? "/app" : "/register"); return; }
    const lookup = annual ? t.yearly_lookup : t.monthly_lookup;
    if (!user) {
      sessionStorage.setItem("kreonis_pending_checkout", JSON.stringify({ lookup, name: t.name }));
      toast.info(`Create your account to continue to ${t.name} checkout`);
      navigate("/register");
      return;
    }
    setLoading(t.id);
    try {
      const { data } = await api.post("/billing/checkout", { lookup_key: lookup, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout unavailable");
      setLoading("");
    }
  };

  return (
    <KLayout>
      <Seo title="KREONIS — Access Tiers" />
      <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6">
        <div className="text-center">
          <div className="flex justify-center"><KDemo /></div>
          <h1 className="mt-3 font-display text-3xl font-bold text-slate-50 sm:text-5xl">Access Tiers</h1>
          <p className="mx-auto mt-3 max-w-xl text-slate-400">Institutional-grade intelligence, priced for every allocator. Cancel anytime.</p>
          <div className="mt-6 inline-flex items-center gap-1 rounded-full border border-slate-800 bg-slate-900 p-1">
            <button onClick={() => setAnnual(false)} data-testid="kprice-monthly" className={`rounded-full px-4 py-1.5 text-sm font-medium ${!annual ? "bg-emerald-500 text-slate-950" : "text-slate-400"}`}>Monthly</button>
            <button onClick={() => setAnnual(true)} data-testid="kprice-annual" className={`rounded-full px-4 py-1.5 text-sm font-medium ${annual ? "bg-emerald-500 text-slate-950" : "text-slate-400"}`}>Annual <span className="text-emerald-300">Save 33%</span></button>
          </div>
        </div>
        <div className="mt-10 grid gap-6 lg:grid-cols-3">
          {tiers.map((t) => {
            const price = t.id === "core" ? "$0" : annual ? `$${t.y}` : `$${t.m}`;
            const per = t.id === "core" ? "Always Free" : annual ? "/year" : "/mo";
            return (
              <div key={t.id} data-testid={`kplan-${t.id}`} className={`relative rounded-2xl border bg-slate-900/60 p-7 ${t.popular ? "border-emerald-500 shadow-xl shadow-emerald-500/10" : "border-slate-800"}`}>
                {t.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-emerald-500 px-3 py-1 text-xs font-semibold text-slate-950 flex items-center gap-1"><Sparkles className="h-3 w-3" /> Most Popular</div>}
                <div className="text-xs font-semibold uppercase tracking-widest text-emerald-400">{t.tagline}</div>
                <h3 className="mt-1 font-display text-xl font-bold text-slate-50">{t.name}</h3>
                <div className="mt-3 flex items-end gap-1"><span className="font-display text-4xl font-extrabold text-slate-50">{price}</span><span className="mb-1 text-sm text-slate-500">{per}</span></div>
                {t.id !== "core" && annual && <div className="mt-1 text-xs text-emerald-400">Save 33% vs monthly</div>}
                <button data-testid={`kplan-${t.id}-cta`} disabled={loading === t.id} onClick={() => choose(t)} className={`mt-5 w-full rounded-lg px-4 py-2.5 text-sm font-semibold disabled:opacity-60 ${t.popular ? "bg-emerald-500 text-slate-950 hover:bg-emerald-400" : "border border-slate-700 text-slate-200 hover:bg-slate-800"}`}>{loading === t.id ? "Redirecting…" : t.cta}</button>
                <ul className="mt-6 space-y-3">
                  {t.features.map((f) => <li key={f} className="flex items-start gap-2 text-sm text-slate-300"><Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" /> {f}</li>)}
                </ul>
              </div>
            );
          })}
        </div>
        <p className="mt-6 text-center text-xs text-slate-600">Secure checkout by Stripe. Test mode: card 4242 4242 4242 4242, any future expiry & CVC.</p>
      </div>
    </KLayout>
  );
}
